import { useCallBlockV1QueryUpdaters } from '../query-updaters/use-query-updaters';
import { BlockedNumber, DeleteBlockedNumberIO } from '../types';
import { UseCallBlockV1MutationEndpointArgs, useCallBlockV1Mutation } from './use-call-block-v1-mutation';

type MutationContext<C = unknown> = {
  removed?: BlockedNumber;
  otherContext?: C;
};

/** The delete request only carries the blocked number's id, so the tenant is passed separately for cache updates. */
type DeleteOtherOptions = { tenantId: string };

/**
 * A hook that returns a mutation for the `DeleteBlockedNumber` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * Call it with `{ id, _otherOptions: { tenantId } }`.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `false`.
 */
export const useDeleteBlockedNumberMutation = <
  E = unknown,
  C = unknown,
  RequestOverride extends DeleteBlockedNumberIO['input'] = DeleteBlockedNumberIO['input']
>({
  options,
  optimisticUpdate = false,
  ...args
}: UseCallBlockV1MutationEndpointArgs<
  'DeleteBlockedNumber',
  E,
  C | undefined,
  DeleteOtherOptions,
  RequestOverride
> = {}) => {
  const {
    cancelBlockedNumbersQueries,
    findBlockedNumber,
    addBlockedNumber,
    removeBlockedNumber,
    invalidateBlockedNumbers,
  } = useCallBlockV1QueryUpdaters();

  return useCallBlockV1Mutation<'DeleteBlockedNumber', E, MutationContext<C>, DeleteOtherOptions, RequestOverride>({
    endpointName: 'DeleteBlockedNumber',
    ...args,
    options: {
      ...options,
      onMutate: async (request) => {
        const tenantId = request._otherOptions?.tenantId;
        let removed: BlockedNumber | undefined;
        if (optimisticUpdate && tenantId) {
          await cancelBlockedNumbersQueries(tenantId);
          removed = findBlockedNumber(tenantId, request.id);
          removeBlockedNumber(tenantId, request.id);
        }

        return {
          removed,
          otherContext: await options?.onMutate?.(request),
        };
      },
      onSuccess: (response, request, context) => options?.onSuccess?.(response, request, context?.otherContext),
      onError: (error, request, context) => {
        const tenantId = request._otherOptions?.tenantId;
        if (context?.removed && tenantId) {
          addBlockedNumber(tenantId, context.removed);
        }

        return options?.onError?.(error, request, context?.otherContext);
      },
      onSettled: (response, error, request, context) => {
        if (request._otherOptions?.tenantId) {
          invalidateBlockedNumbers(request._otherOptions.tenantId);
        }

        // Only pass context of type C into provided `onSettled` option
        return options?.onSettled?.(response, error, request, context?.otherContext);
      },
    },
  });
};
